<script setup lang="ts">
import FormInput from '../../ui/form/TextInput.vue'
import SearchSelect from '../../ui/form/SearchSelect.vue'
import NumberInput from '../../ui/form/NumberInput.vue'
import PhoneInput from '../../ui/form/PhoneInput.vue'
import { useFormValidation } from '../../../composables/useFormValidation';
import { required } from '../../../utils/validation';
import { email } from '../../../utils/validation';
import { formatNumber } from '../../../utils/CommonFunctions';

const props = defineProps<{
  data?: Record<string, any>
}>()

const emit = defineEmits<{
  (e: 'complete', data: any): void
  (e: 'signin'): void
}>()

const { fields, formData, setFieldRules, validateForm } = useFormValidation({
  company_name: props.data?.company_name || '',
  business_types_id: props.data?.business_types_id || '',
  countrys_id: props.data?.countrys_id || '',
  number_of_employees: props.data?.number_of_employees || 1,
  primary_contact_person: props.data?.primary_contact_person || '',
  primary_email: props.data?.primary_email || '',
  primary_contact_number: props.data?.primary_contact_number || '',
  phone_country_code: props.data?.phone_country_code || '',
  no_of_outlets: props.data?.no_of_outlets || 1,
  require_accounts: props.data?.require_accounts || false,
  require_crm: props.data?.require_crm || false,
  require_hrm: props.data?.require_hrm || false,
  contact_us: props.data?.contact_us || false,
  referred_by: props.data?.referred_by || '',
  remarks: props.data?.remarks || '',
  packages_id: props.data?.packages_id || 'trial'
})

// Business Types
const businessTypes = [
  { id: '1', name: 'Sole Proprietorship' },
  { id: '2', name: 'Partnership' },
  { id: '3', name: 'Corporation' },
  { id: '4', name: 'Limited Liability Company (LLC)' },
  { id: '5', name: 'Non-Profit Organization' }
]

// Countries with flags and phone codes
const countries = [
  { id: '1', name: 'Maldives', flag: 'MV', code: '+960' },
]

// Referral Sources
const referralSources = [
  { id: 'search', name: 'Search Engine' },
  { id: 'social', name: 'Social Media' },
  { id: 'friend', name: 'Friend/Colleague' },
  { id: 'advertisement', name: 'Advertisement' },
  { id: 'other', name: 'Other' }
]

// Package options
const packages = [
  {
    id: '1',
    name: 'Trial',
    description: '30-day free trial with basic features',
    price: { monthly: 0, yearly: 0 }
  },
  {
    id: '2',
    name: 'Starter',
    description: 'Perfect for small businesses',
    price: { monthly: 29, yearly: 290 }
  },
  {
    id: '3',
    name: 'Professional',
    description: 'Advanced features for growing businesses',
    price: { monthly: 79, yearly: 790 }
  },
  {
    id: '4',
    name: 'Enterprise',
    description: 'Complete solution for large organizations',
    price: { monthly: 199, yearly: 1990 }
  }
]

// Set validation rules
setFieldRules('company_name', [required('Company name is required')])
setFieldRules('business_types_id', [required('Business type is required')])
setFieldRules('countrys_id', [required('Country is required')])
setFieldRules('number_of_employees', [required('Number of employees is required')])
setFieldRules('primary_contact_person', [required('Primary contact person is required')])
setFieldRules('primary_email', [required('Email is required'), email()])
setFieldRules('primary_contact_number', [required('Contact number is required')])
setFieldRules('no_of_outlets', [required('Number of outlets is required')])
setFieldRules('packages_id', [required('Please select a package')])
setFieldRules('referred_by', [required('Please select a Referred by')])

const handleSubmit = () => {
  if (validateForm()) {
    emit('complete', formData.value)
  }
}

</script>

<template>
  <form @submit.prevent="handleSubmit" class="space-y-6">
    <h2 class="text-xl font-semibold text-gray-900 dark:text-white mb-6">Company Registration Details</h2>
    <div class="grid md:grid-cols-2 gap-6">
      
      <!-- Left Column -->
      <div class="space-y-6">
        <FormInput
          v-model="fields.company_name.value"
          label="Company Name"
          :error="fields.company_name.touched ? fields.company_name.error : null"
          @blur="fields.company_name.touched = true"
        />

        <SearchSelect
          v-model="fields.business_types_id.value"
          :options="businessTypes"
          label="Business Type"
          placeholder="Select business type"
          :error="fields.business_types_id.touched ? fields.business_types_id.error : null"
          @blur="fields.business_types_id.touched = true"
        />

        <SearchSelect
          v-model="fields.countrys_id.value"
          :options="countries"
          label="Country"
          placeholder="Select country"
          :error="fields.countrys_id.touched ? fields.countrys_id.error : null"
          @blur="fields.countrys_id.touched = true"
        />

        <NumberInput
          v-model="fields.number_of_employees.value"
          type="number"
          label="Number of Employees"
          :min="1"
          :error="fields.number_of_employees.touched ? fields.number_of_employees.error : null"
          @blur="fields.number_of_employees.touched = true"
        />

        <FormInput
          v-model="fields.primary_contact_person.value"
          label="Primary Contact Person"
          :error="fields.primary_contact_person.touched ? fields.primary_contact_person.error : null"
          @blur="fields.primary_contact_person.touched = true"
        />

        <FormInput
          v-model="fields.primary_email.value"
          type="email"
          label="Primary Email"
          :error="fields.primary_email.touched ? fields.primary_email.error : null"
          @blur="fields.primary_email.touched = true"
        />
      </div>

      <!-- Right Column -->
      <div class="space-y-6">
        <!-- Phone Number with Country Code -->
        <PhoneInput
          v-model="fields.primary_contact_number.value"
          v-model:countryCode="fields.phone_country_code.value"
          label="Primary Contact Number"
          placeholder="Enter phone number"
          :error="fields.primary_contact_number.touched ? fields.primary_contact_number.error : null"
          @blur="fields.primary_contact_number.touched = true"
        />

        <NumberInput
          v-model="fields.no_of_outlets.value"
          type="number"
          label="Number of Outlets"
          :min="1"
          :error="fields.no_of_outlets.touched ? fields.no_of_outlets.error : null"
          @blur="fields.no_of_outlets.touched = true"
        />

        <!-- Checkboxes -->
        <div class="space-y-3">
          <label class="flex items-center">
            <input
              v-model="fields.require_accounts.value"
              type="checkbox"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">Require Accounts Module</span>
          </label>

          <label class="flex items-center">
            <input
              v-model="fields.require_crm.value"
              type="checkbox"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">Require CRM Module</span>
          </label>

          <label class="flex items-center">
            <input
              v-model="fields.require_hrm.value"
              type="checkbox"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">Require HRM Module</span>
          </label>

          <label class="flex items-center">
            <input
              v-model="fields.contact_us.value"
              type="checkbox"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">Contact me about the product</span>
          </label>
        </div>

        <SearchSelect
          v-model="fields.referred_by.value"
          :options="referralSources"
          label="How did you hear about us?"
          placeholder="Select referral source"
          :error="fields.referred_by.touched ? fields.referred_by.error : null"
          @blur="fields.referred_by.touched = true"
        />

        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Remarks
          </label>
          <textarea
            v-model="fields.remarks.value"
            rows="3"
            maxlength="500"
            class="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
            placeholder="Additional comments or requirements..."
          ></textarea>
          <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {{ fields.remarks.value.length }}/500 characters
          </p>
        </div>
      </div>
    </div>

    <!-- Package Selection -->
    <div class="mt-8">
      <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Select Package</h3>
      <div class="grid md:grid-cols-4 gap-4">
        <label
          v-for="pkg in packages"
          :key="pkg.id"
          class="relative border-2 rounded-lg p-4 cursor-pointer transition-colors"
          :class="[
            fields.packages_id.value === pkg.id
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
          ]"
        >
          <input
            type="radio"
            v-model="fields.packages_id.value"
            :value="pkg.id"
            class="sr-only"
          />
          <div class="text-center">
            <h4 class="font-medium text-gray-900 dark:text-white">{{ pkg.name }}</h4>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">{{ pkg.description }}</p>
            <div class="mt-2">
              <p class="text-lg font-bold text-gray-900 dark:text-white">
                {{ pkg.price.monthly > 0 ? `MVR ${formatNumber(pkg.price.monthly)}/mo` : 'Free' }}
              </p>
              <p class="text-sm text-gray-500 dark:text-gray-400">
                {{ pkg.price.yearly > 0 ? `MVR ${formatNumber(pkg.price.yearly)}/yr` : '' }}
              </p>
            </div>
          </div>
        </label>
      </div>
    </div>

    <div class="flex justify-between items-center">
      <p class="text-sm text-gray-600 dark:text-gray-400">
        have an account?
        <button
          type="button"
          @click="$emit('signin')"
          class="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
        >
          Sign in
        </button>
      </p>

      <button
        type="submit"
        class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
      >
        Next Step
      </button>
    </div>
  </form>
</template>
```